import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import styles from './Checkbox.scss';

/*
 * Form control component; ensures an accessible label, even when label text is
 * not intended to be displayed visually.
 *
 * `labelText` may be plain text, or a simple element. The `visuallyHiddenLabel`
 * flag is useful if design calls for a more complicated element.
 *
 * Because `labelText` will be inside the input’s `<label>`, whatever you pass in
 * will also be clickable.
 */
class Checkbox extends React.Component {
    state = {
        checked: this.props.checked,
    };

    componentDidUpdate(prevProps) {
        const {
            checked,
        } = this.props;

        if (prevProps.checked !== checked) {
            this.setState(() => ({
                checked,
            }));
        }
    }

    onClick = () => {
        const {
            delayChange,
            disabled,
            onChange,
        } = this.props;

        if (!disabled) {
            if (!delayChange) {
                return onChange(!this.state.checked);
            }

            this.setState((state) => ({
                checked: !state.checked,
            }), () => {
                setTimeout(() => {
                    onChange(this.state.checked);
                }, 0);
            });
        }

        return undefined;
    };

    render() {
        const { checked } = this.state;
        const {
            checkboxClassName,
            className,
            disabled,
            indeterminate,
            labelClassName,
            labelText,
            visuallyHiddenLabel,
        } = this.props;

        return (
            <label
                className={classNames(
                    className,
                    styles.label,
                    { [styles.disabled]: disabled },
                )}
            >
                <input
                    checked={checked}
                    className={styles.input}
                    disabled={disabled}
                    onClick={this.onClick}
                    // eslint-disable-next-line
                    ref={(el) => el && (el.indeterminate = indeterminate)}
                    type="checkbox"
                    readOnly
                />
                <span
                    className={classNames(
                        checkboxClassName,
                        styles.checkbox,
                        { [styles.checked]: checked },
                        { [styles.indeterminate]: indeterminate },
                        { [styles.disabled]: disabled },
                    )}
                />
                <span
                    className={classNames(
                        labelClassName,
                        { [styles.hidden]: visuallyHiddenLabel },
                    )}
                    hidden={visuallyHiddenLabel}
                >
                    {labelText}
                </span>
            </label>
        );
    }
}

Checkbox.propTypes = {
    checkboxClassName: PropTypes.string,
    checked: PropTypes.bool,
    className: PropTypes.string,
    delayChange: PropTypes.bool,
    disabled: PropTypes.bool,
    indeterminate: indeterminatePropCheck,
    labelClassName: PropTypes.string,
    labelText: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element,
    ]), // todo: make prop required
    onChange: PropTypes.func,
    visuallyHiddenLabel: PropTypes.bool,
};

Checkbox.defaultProps = {
    checked: true,
    delayChange: false,
    disabled: false,
    indeterminate: false,
    onChange() { },
    visuallyHiddenLabel: false,
};

export function indeterminatePropCheck(props, propName, componentName) {
    if (typeof props[propName] !== 'boolean') {
        return new Error(
            `Invalid prop ${propName} supplied to ${componentName}. Must be a boolean.`,
        );
    }
    if (props.checked && props[propName]) {
        return new Error(
            `Invalid prop ${propName} supplied to ${componentName}.
            Checked and indeterminate cannot both be true.`,
        );
    }

    return undefined;
}

export default Checkbox;
